import { useEffect, useState } from "react";
import {
  calculateRiskScore,
  getColorProbability,
  getColorScore,
  getImageURLScore,
  getLetterScore,
} from "../../../utils/utils";
import ReactApexChart from "react-apexcharts";
import { HiExclamationCircle, HiShieldCheck } from "react-icons/hi";

function getScoreBarColor(score) {
  if (score >= 800) return "bg-green-500";
  if (score >= 600) return "bg-yellow-500";
  return "bg-red-500";
}

function getProbabilityBarColor(probability) {
  const percent = probability / 100;
  if (percent <= 20) return "bg-green-500";
  if (percent <= 50) return "bg-yellow-500";
  return "bg-red-500";
}

export const EvaluationScore = ({
  data,
  score,
  probability,
  decisao = null,
  hasIncome = true,
}) => {
  const [riskScore, setRiskScore] = useState(0);

  useEffect(() => {
    setRiskScore(
      calculateRiskScore(
        +data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal || 0,
        +data?.resumoConsulta?.protestos?.quantidadeTotal || 0,
        +score,
        +probability
      )
    );
  }, [data]);

  function getMintScoreColor(score) {
    if (score >= 751) return "#A6F2CF"; // bom
    if (score >= 501) return "#FFE0B5"; // médio
    return "#F4B9B8"; // ruim
  }

  return (
    <div className="space-y-3">
      {/* Bloco compacto: Score + Probabilidade lado a lado */}
      <div className="grid grid-cols-2 gap-4">
        {/* Card Score com barra de cor forte */}
        <div className="p-6 bg-white rounded-2xl border border-gray-200 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <HiShieldCheck className="text-green-500" size={20} />
            <span className="text-sm font-medium text-gray-600">
              Score de Risco
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-4xl font-extrabold text-gray-800">{score}</div>
          </div>

          <div className="text-xs text-gray-500 -mt-2">Escala de 0 a 1000</div>

          <div className="relative w-full h-6 bg-gray-200 rounded-full overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-yellow-400 to-green-500 rounded-full"></div>
            <div
              className="absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 border-white bg-white z-10 transition-all duration-700 ease-out"
              style={{ left: `calc(${(score / 1000) * 100}% - 12px)` }}
            ></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-semibold text-white drop-shadow">
                {score}
              </span>
            </div>
          </div>
        </div>

        {/* Card Probabilidade com barra de cor forte */}
        <div
          className={`p-6 rounded-2xl border border-gray-200 flex flex-col gap-3 ${
            parseInt(probability / 100) < 30
              ? "bg-green-50"
              : parseInt(probability / 100) < 55
              ? "bg-orange-50"
              : "bg-red-50"
          }`}
        >
          <div className="flex items-center gap-2">
            <HiExclamationCircle className="text-red-500" size={20} />
            <span className="text-sm font-medium text-gray-600">
              Probabilidade de Inadimplência
            </span>
          </div>

          <div className="text-4xl font-extrabold text-gray-800">
            {parseInt(probability / 100)}%
          </div>

          <div className="text-xs text-gray-500 -mt-2">
            Quanto menor, melhor
          </div>

          <div className="relative w-full h-6 bg-gray-200 rounded-full overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-green-500 via-yellow-400 to-red-500 rounded-full"></div>
            <div
              className="absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 border-white bg-white z-10 transition-all duration-700 ease-out"
              style={{
                left: `calc(${parseInt(probability / 100)}% - 12px)`,
              }}
            ></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-semibold text-white drop-shadow">
                {parseInt(probability / 100)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Aviso Legal */}
      <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-xl text-sm text-gray-700">
        <strong>Importante:</strong> Este relatório é apenas informativo e não
        substitui a avaliação de crédito da instituição concedente.
      </div>

      {hasIncome && (
        <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
          <div className="text-xs font-semibold text-blue-700 mb-1">
            Renda Presumida
          </div>
          <div className="text-xl font-bold text-gray-800">
            {data?.dadosCadastrais?.rendaPresumida ||
              "Sem informação disponível"}
          </div>
          {data?.dadosCadastrais?.textoRendaPresumida &&
            data.dadosCadastrais.textoRendaPresumida !==
              data.dadosCadastrais.rendaPresumida && (
            <div className="text-xs text-gray-600 mt-1">
              {data?.dadosCadastrais?.textoRendaPresumida}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
